import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User } from "@appTypes/index";
import getTpUser from "@api/getTpUser";
import { APP_BASE_URL } from "@config";
import { navigateTo } from "@utils/navigate";
import { USER_TYPE_PREMIUM, USER_TYPE_BASIC, USER_TYPE_COACHED_ATHLETE } from "@constants/user";

const VALID_USER_TYPES = [USER_TYPE_PREMIUM, USER_TYPE_BASIC, USER_TYPE_COACHED_ATHLETE];

export interface UserContextType {
  user: User | null;
  isUserLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const defaultContext: UserContextType = {
  user: null,
  isUserLoading: true,
  error: null,
  refresh: () => undefined,
};

export const UserContext = createContext<UserContextType>(defaultContext);

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const controller = new AbortController();

    const fetchUser = async () => {
      const response = await getTpUser(controller.signal);

      if (response.error === "AbortError") return;

      if (response.success && response.data) {
        if (!response.data.user.userType || !VALID_USER_TYPES.includes(response.data.user.userType)) {
          setIsUserLoading(false);
          navigateTo(`${APP_BASE_URL}/#home`);
          return;
        }
        setUser(response.data.user);
      } else {
        setError(response.error);
      }
      setIsUserLoading(false);
    };

    fetchUser();
    return () => controller.abort();
  }, [refreshKey]);

  return (
    <UserContext.Provider value={{ user, isUserLoading, error, refresh }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
